<template>
  <div class="form-section mb-0">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="form-subtitle mb-0">Legajo digital</h2>
      <button type="button" class="button button-primary" @click="showUploadModal = true">
        <i class="ri-upload-2-line mr-1"></i> Subir documento
      </button>
    </div>

    <!-- Tabla de documentos -->
    <div v-if="documents.length === 0 && !loading" class="text-center py-4 text-muted">
      No hay documentos registrados
    </div>

    <table v-if="documents.length > 0" class="custom-table w-100">
      <thead>
        <tr>
          <th>TIPO</th>
          <th>ARCHIVO</th>
          <th>EXT.</th>
          <th>TAMAÑO</th>
          <th>FECHA SUBIDA</th>
          <th>OBSERVACIONES</th>
          <th>OPCIONES</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="doc in documents" :key="doc.id">
          <td>{{ doc.documentType ? doc.documentType.name : '—' }}</td>
          <td>{{ doc.fileName }}</td>
          <td>{{ doc.fileExtension }}</td>
          <td>{{ formatSize(doc.fileSize) }}</td>
          <td>{{ formatDate(doc.createdAt) }}</td>
          <td>{{ doc.notes || '—' }}</td>
          <td class="cell-center d_option pr-md-2 position-relative">
            <DropdownTable>
              <template slot="dropdown-content">
                <DropdownItem text="Ver" @click="previewDoc(doc)" />
                <DropdownItem text="Descargar" @click="downloadDoc(doc)" />
                <DropdownItem text="Eliminar" @click="confirmDelete(doc)" custom-class="text-danger" />
              </template>
            </DropdownTable>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal de carga -->
    <b-modal
      v-model="showUploadModal"
      title="Subir documento"
      hide-footer
      centered
      @hidden="resetUploadForm"
    >
      <div class="form-group">
        <label>Tipo de documento *</label>
        <FormSelect
          id="uploadDocumentType"
          v-model="uploadForm.documentTypeId"
          :items="availableDocumentTypes"
          defaultOption="Elegir tipo de documento"
        />
      </div>

      <div class="form-group">
        <label>Archivo * <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 10MB)</small></label>
        <FileInput
          class="w-100 mt-2"
          id-file="employee-doc-upload"
          :acceptFile="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onFileChange"
        />
      </div>

      <div class="form-group">
        <label class="label">Observaciones</label>
        <textarea
          v-model="uploadForm.notes"
          class="form-control w-100"
          rows="3"
          placeholder="Ingresar observaciones (opcional)"
        />
      </div>

      <div v-if="allTypesUploaded" class="alert alert-info mb-3">
        <i class="ri-checkbox-circle-line mr-1"></i>
        Todos los tipos de documento ya han sido cargados.
      </div>

      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="button button-light mr-2" @click="showUploadModal = false">
          Cancelar
        </button>
        <button type="button" class="button button-primary" @click="uploadDocument" :disabled="uploading || allTypesUploaded">
          {{ uploading ? 'Subiendo...' : 'Subir' }}
        </button>
      </div>
    </b-modal>

    <!-- Modal de preview -->
    <b-modal
      v-model="showPreviewModal"
      :title="previewTitle"
      size="xl"
      hide-footer
      centered
      @hidden="onPreviewHidden"
    >
      <div class="text-center">
        <img
          v-if="previewType === 'image'"
          :src="previewUrl"
          class="img-fluid"
          style="max-height: 75vh"
        />
        <embed
          v-else-if="previewType === 'pdf'"
          :src="previewUrl"
          type="application/pdf"
          width="100%"
          style="height: 75vh"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, FormSelect, FileInput, DropdownTable, DropdownItem } from 'wize-admin'
import { DocumentTypeService, EmployeeDocumentService } from '@/core/services'

export default {
  components: { FormSelect, FileInput, DropdownTable, DropdownItem },
  props: {
    employeeId: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      uploading: false,
      documents: [],
      documentTypeList: [],
      showUploadModal: false,
      uploadForm: {
        documentTypeId: null,
        file: null,
        notes: '',
      },
      // Preview
      showPreviewModal: false,
      previewUrl:       null,
      previewType:      null,
      previewTitle:     null,
    }
  },
  computed: {
    uploadedTypeIds() {
      return this.documents
        .filter(d => d.documentType)
        .map(d => d.documentType.id)
    },
    availableDocumentTypes() {
      return this.documentTypeList.filter(t => !this.uploadedTypeIds.includes(t.key))
    },
    allTypesUploaded() {
      return this.documentTypeList.length > 0 && this.availableDocumentTypes.length === 0
    },
  },
  watch: {
    employeeId(val) {
      if (val) this.loadDocuments()
    },
  },
  methods: {
    async loadDocuments() {
      if (!this.employeeId) return
      try {
        this.loading = true
        this.documents = (await EmployeeDocumentService.list(this.employeeId)).payload || []
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loading = false
      }
    },
    async loadDocumentTypes() {
      try {
        const resp = (await DocumentTypeService.list()).payload || []
        this.documentTypeList = resp.map(d => ({ key: d.id, label: d.name }))
      } catch (ex) {
        console.error(ex)
      }
    },
    onFileChange(files) {
      this.uploadForm.file = files && files.length > 0 ? files[0] : null
    },
    async uploadDocument() {
      if (!this.uploadForm.documentTypeId) {
        Alert.error('Selecciona el tipo de documento')
        return
      }
      if (!this.uploadForm.file) {
        Alert.error('Selecciona un archivo')
        return
      }
      try {
        this.uploading = true
        const formData = new FormData()
        formData.append('employeeId', this.employeeId)
        formData.append('documentTypeId', this.uploadForm.documentTypeId)
        formData.append('file', this.uploadForm.file)
        if (this.uploadForm.notes) formData.append('notes', this.uploadForm.notes)
        await EmployeeDocumentService.upload(formData)
        Alert.success('Documento subido correctamente')
        this.showUploadModal = false
        await this.loadDocuments()
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al subir el documento')
        console.error(ex)
      } finally {
        this.uploading = false
      }
    },
    async previewDoc(doc) {
      const ext = (doc.fileExtension || '').toLowerCase()
      const isImage = ['jpg', 'jpeg', 'png'].includes(ext)
      const isPdf   = ext === 'pdf'

      if (!isImage && !isPdf) {
        Alert.error('Formato no soportado para vista previa. Usa el botón Descargar.')
        return
      }

      const mimeTypes = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', pdf: 'application/pdf' }
      const mimeType = mimeTypes[ext] || 'application/octet-stream'

      try {
        const response = await EmployeeDocumentService.download(doc.id)
        const blob = response instanceof Blob ? response : new Blob([response.data || response], { type: mimeType })
        this.previewUrl   = window.URL.createObjectURL(blob)
        this.previewType  = isImage ? 'image' : 'pdf'
        this.previewTitle = doc.fileName
        this.showPreviewModal = true
      } catch (ex) {
        Alert.error('Error al cargar la vista previa')
        console.error(ex)
      }
    },
    onPreviewHidden() {
      if (this.previewUrl) window.URL.revokeObjectURL(this.previewUrl)
      this.previewUrl  = null
      this.previewType = null
    },
    async downloadDoc(doc) {
      try {
        const response = await EmployeeDocumentService.download(doc.id)
        const blob = response instanceof Blob ? response : response.data
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', doc.fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (ex) {
        Alert.error('Error al descargar el documento')
        console.error(ex)
      }
    },
    async confirmDelete(doc) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `¿Eliminar el documento "${doc.fileName}"?`,
        { title: 'Confirmar eliminación', okTitle: 'Eliminar', cancelTitle: 'Cancelar', okVariant: 'danger' }
      )
      if (!ok) return
      try {
        await EmployeeDocumentService.delete(doc.id)
        Alert.success('Documento eliminado')
        await this.loadDocuments()
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al eliminar')
      }
    },
    resetUploadForm() {
      this.uploadForm = { documentTypeId: null, file: null, notes: '' }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('es-PE')
    },
    formatSize(bytes) {
      if (!bytes) return '—'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
  },
  async created() {
    await this.loadDocumentTypes()
    if (this.employeeId) await this.loadDocuments()
  },
}
</script>

<style lang="stylus" scoped></style>
